import { useState } from "react";
import { Receipt, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, notifyErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/shared";
import { ConnectionPanel, IssuerPanel, InvoiceArchive, ChargesPanel, ProviderCatalog, InvoiceWizard, InvoiceDetail, InvBadge, useInvoicingConn } from "@/components/Invoicing";

const SENT = ["xml_esportato", "inviata_esternamente", "invio_in_corso", "presa_in_carico", "errore_elaborazione", "inviata", "scartata", "non_consegnata",
  "recapito_impossibile", "consegnata", "accettata", "rifiutata", "decorrenza_termini", "da_verificare"];
const LABEL = { xml_esportato: "XML esportato – invio esterno da completare", inviata_esternamente: "Inviata esternamente", invio_in_corso: "Invio in corso",
  presa_in_carico: "Presa in carico", inviata: "Inviata", consegnata: "Consegnata", accettata: "Accettata", rifiutata: "Rifiutata", scartata: "Scartata",
  non_consegnata: "Non consegnata", recapito_impossibile: "Recapito impossibile", decorrenza_termini: "Decorrenza termini", da_verificare: "Invio da verificare",
  errore_elaborazione: "Errore elaborazione" };
let connPromise = null;
const tenantConn = () => (connPromise = connPromise || api.get("/invoicing/connection").then((r) => r.data).catch(() => ({})));

export function RentalInvoiceButton({ r, size = "sm" }) {
  const { state, can } = useAuth();
  const [wiz, setWiz] = useState(null);
  const [view, setView] = useState(false);
  const [busy, setBusy] = useState(false);
  const [conn, setConn] = useState(null);
  if (!state.features?.einvoice || r.imported) return null;
  if (r.invoice_status && SENT.includes(r.invoice_status)) {
    return <span className="inline-flex items-center gap-2" data-testid={`rental-invoice-state-${r.id}`}>
      <InvBadge inv={{ status_internal: r.invoice_status, status_label: LABEL[r.invoice_status] }} />
      {r.invoice_number && <span className="font-mono text-xs">{r.invoice_number}</span>}
      {can("invoices", "view") && <Button size={size} variant="outline" onClick={(e) => { e.stopPropagation(); setView(true); }} data-testid={`rental-invoice-view-${r.id}`}>Visualizza fattura</Button>}
      {view && <InvoiceDetail ibase="/invoices" id={r.invoice_id} canManage={can("invoices", "create")} onClose={() => setView(false)} />}
    </span>;
  }
  if (!can("invoices", "create") || r.status === "annullato" || !(Number(r.price) > 0)) return null;
  const open = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const c = conn || await tenantConn();
      setConn(c);
      if (!c.provider_key) { toast.error("Collega prima un portale di fatturazione (oppure XML manuale) in Fatture › Collegamento portale"); return; }
      const x = await api.post(`/contracts/${r.id}/invoice/draft`);
      if (x.data.existing && SENT.includes(x.data.status_internal)) { toast.info(x.data.message); return; }
      setWiz(x.data);
    } catch (err) { notifyErr(err); } finally { setBusy(false); }
  };
  return <>
    <Button size={size} className="btn-brand" disabled={busy} onClick={open} data-testid={`rental-invoice-btn-${r.id}`}>{busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Receipt size={14} className="mr-1" />}Fattura</Button>
    {wiz && <InvoiceWizard ibase="/invoices" invoice={wiz} tenant direct={!!conn?.capabilities?.direct_send} onClose={() => { setWiz(null); window.dispatchEvent(new Event("invoice-updated")); }} />}
  </>;
}

export default function InvoicingPage() {
  const { state, can } = useAuth();
  const platform = state.platform;
  const conn = useInvoicingConn(platform ? "/admin/invoicing" : "/invoicing");
  if (!platform && !state.features?.einvoice) {
    return <div><PageHeader title="Fatture" /><p className="text-sm text-zinc-400" data-testid="einv-upper-only">Funzione disponibile con il Piano Upper.</p></div>;
  }
  const direct = !!conn?.capabilities?.direct_send;
  const admin = platform || ["admin", "superadmin"].includes(state.user?.role);
  const tabs = platform
    ? [["charges", "Abbonamenti NOLIUM", () => <ChargesPanel direct={direct} />], ["archive", "Archivio fatture", () => <InvoiceArchive ibase="/admin/invoices" canManage direct={direct} />],
      ["connection", "Collegamento portale", () => <ConnectionPanel base="/admin/invoicing" />], ["issuer", "Emittente NOLIUM", () => <IssuerPanel base="/admin/invoicing" platform />],
      ["catalog", "Catalogo provider", ProviderCatalog]]
    : [["archive", "Fatture", () => <InvoiceArchive ibase="/invoices" tenant canManage={can("invoices", "create")} direct={direct} />],
      ...(admin ? [["connection", "Collegamento portale", () => <ConnectionPanel base="/invoicing" />], ["issuer", "Dati fiscali e numerazione", () => <IssuerPanel base="/invoicing" />]] : [])];
  return (
    <div data-testid="invoicing-page">
      <PageHeader title={platform ? "Fatturazione NOLIUM" : "Fatturazione elettronica"} subtitle={platform ? "Fatture NOLIUM verso le società clienti (account portale globale)" : "Fatture dei tuoi contratti di noleggio, con il tuo portale di fatturazione"} />
      <Tabs defaultValue={tabs[0][0]}>
        <TabsList className="flex-wrap h-auto mb-2">{tabs.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`einv-tab-${k}`}>{l}</TabsTrigger>)}</TabsList>
        {tabs.map(([k, , C]) => <TabsContent key={k} value={k}><C /></TabsContent>)}
      </Tabs>
    </div>
  );
}
